/**
 * SimultaneousSessionModal - Confirmation modal for simultaneous session booking
 *
 * Shown when:
 * 1. Matinee threshold triggers simultaneous mode (both sessions at same time)
 * 2. Studio manually checks "run simultaneously" checkbox
 */

interface SimultaneousSessionModalProps {
  trigger: 'matinee_threshold' | 'manual_checkbox' | 'distance_threshold';
  cityName?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function SimultaneousSessionModal({
  trigger,
  cityName,
  onConfirm,
  onCancel,
}: SimultaneousSessionModalProps) {
  let message: string;
  if (trigger === 'matinee_threshold') {
    message = `Please note: due to matinee time constraints in ${cityName || 'this city'}, both class slots must run simultaneously. You will need two separate studios available at the same time. Our team will confirm this with you after booking.`;
  } else if (trigger === 'distance_threshold') {
    message = 'Please note: because this venue is over 40 miles from your studio, both class slots must run simultaneously to fit the schedule. You will need two separate studios available at the same time. Our team will confirm this with you after booking.';
  } else {
    message = 'Please note: you have selected to run both class slots simultaneously. You will need two separate studios available at the same time. Our team will confirm this with you after booking.';
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-xl shadow-xl p-6 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-6">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
            <svg className="h-5 w-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-warm-900 mb-2">Simultaneous Class Slots</h3>
            <p className="text-sm text-warm-600 leading-relaxed">{message}</p>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-4 py-2.5 rounded-lg border-2 border-warm-300 text-warm-700 font-medium hover:bg-warm-50 transition-colors"
          >
            Go Back
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex-1 px-4 py-2.5 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 transition-colors"
          >
            I Understand, Continue
          </button>
        </div>
      </div>
    </div>
  );
}

export default SimultaneousSessionModal;
